// Phase-0 spike — Speechmatics realtime + diarization.
//
// Flow: mint a short-lived JWT from the management API (the CORS-risk step),
// then open the realtime WebSocket with `?jwt=`, send StartRecognition with
// diarization, stream PCM, and read speaker-tagged AddTranscript messages.
// Speaker label lives at results[].alternatives[].speaker (e.g. "S1").

import type { LiveSession, SessionEvents, Word } from './types'

const MP_URL = 'https://mp.speechmatics.com/v1/api_keys?type=rt'

export type SpeechmaticsRegion = 'eu' | 'us'

export async function mintSpeechmaticsJwt(apiKey: string): Promise<string> {
  const res = await fetch(MP_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`
    },
    body: JSON.stringify({ ttl: 3600 })
  })
  if (!res.ok) {
    throw new Error(`token mint failed: HTTP ${res.status} ${await res.text()}`)
  }
  const json = (await res.json()) as { key_value?: string }
  if (!json.key_value) throw new Error('token mint: no key_value in response')
  return json.key_value
}

interface SmAlternative {
  content: string
  speaker?: string
  confidence?: number
}
interface SmResult {
  type?: string
  alternatives?: SmAlternative[]
}
interface SmMessage {
  message: string
  reason?: string
  type?: string
  results?: SmResult[]
}

function toWords(results: SmResult[] | undefined, isFinal: boolean): Word[] {
  const words: Word[] = []
  for (const r of results ?? []) {
    const alt = r.alternatives?.[0]
    if (!alt) continue
    words.push({ text: alt.content, speaker: alt.speaker ?? '?', isFinal })
  }
  return words
}

export interface ConnectSpeechmaticsOpts {
  jwt: string
  region: SpeechmaticsRegion
  sampleRate: number
}

export function connectSpeechmatics(
  opts: ConnectSpeechmaticsOpts,
  ev: SessionEvents
): LiveSession {
  const url = `wss://${opts.region}.rt.speechmatics.com/v2?jwt=${opts.jwt}`
  const ws = new WebSocket(url)
  ws.binaryType = 'arraybuffer'

  let ready = false
  let seqNo = 0
  const queue: ArrayBuffer[] = []
  const finalWords: Word[] = []

  ev.onState('connecting', `dialing ${opts.region}.rt.speechmatics.com`)

  ws.onopen = () => {
    ev.onLog('ws open — sending StartRecognition')
    ws.send(
      JSON.stringify({
        message: 'StartRecognition',
        audio_format: {
          type: 'raw',
          encoding: 'pcm_s16le',
          sample_rate: opts.sampleRate
        },
        transcription_config: {
          language: 'en',
          diarization: 'speaker',
          max_delay: 1.0,
          enable_partials: true
        }
      })
    )
  }

  ws.onmessage = (e: MessageEvent<string>) => {
    const msg = JSON.parse(e.data) as SmMessage
    switch (msg.message) {
      case 'RecognitionStarted':
        ready = true
        ev.onState('live', 'recognition started')
        for (const buf of queue) {
          ws.send(buf)
          seqNo++
        }
        queue.length = 0
        break
      case 'AddPartialTranscript':
        ev.onWords(finalWords, toWords(msg.results, false))
        break
      case 'AddTranscript':
        finalWords.push(...toWords(msg.results, true))
        ev.onWords(finalWords, [])
        break
      case 'EndOfTranscript':
        ev.onState('closed', 'end of transcript')
        ws.close()
        break
      case 'Error':
        ev.onState('error', `${msg.type ?? 'error'}: ${msg.reason ?? ''}`)
        break
      case 'Warning':
        ev.onLog(`warning: ${msg.type ?? ''} ${msg.reason ?? ''}`)
        break
      default:
        break
    }
  }

  ws.onerror = () => ev.onState('error', 'websocket error')
  ws.onclose = (e) => {
    if (ready) ev.onState('closed', `socket closed (${e.code})`)
    else ev.onState('error', `closed before start (${e.code}) — check JWT/region`)
  }

  return {
    sendPcm(pcm) {
      if (!ready) {
        queue.push(pcm)
        return
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(pcm)
        seqNo++
      }
    },
    stop() {
      if (ws.readyState === WebSocket.OPEN) {
        ev.onState('closing', 'sending EndOfStream')
        ws.send(JSON.stringify({ message: 'EndOfStream', last_seq_no: seqNo }))
      } else {
        ws.close()
      }
    }
  }
}
